import types, { MINIMUM_WAIT_FOR_RESPONSE, WAIT_TO_SHOW_CONTENT } from "./types";
import { SubmissionError } from "redux-form";
import { List } from "immutable";
import request from "axios";

import { GIVING_FORM_PAGINATION_SCOPE } from "../scopes";
import { nextPage, toggleModal } from "../routes/actions";
import { getSearchQuery, getSelectedCategory } from "./selectors";

import {
  nextPage as nextPaginationPage,
  getPage,
  reset as resetPagination,
  setInitialPagination,
  togglePaginationLoading,
} from "data/pagination";

import handleProcessorErrors from "data/api/handle-processor-errors";
import { bacsDebitCheckout } from "shared/api/giving-form-api/stripeAPI";

export const setGatewayError = (err) => {
  return {
    type: types.SET_GATEWAY_ERROR,
    err,
  };
};
export const selectCategory = (category) => {
  return {
    type: types.SELECT_CATEGORY,
    category,
  };
};

export function partialResetModalSearchState() {
  return {
    type: types.MODAL_RESET_PARTIAL_STATE,
  };
}

export function setUserId(id) {
  return {
    type: types.MODAL_SET_USER_ID,
    id,
  };
}

export function setQuery(query) {
  return {
    type: types.MODAL_SET_QUERY,
    query,
  };
}

export function setToAverageDonation(amount, amountPerCause) {
  return {
    type: types.MODAL_SET_TO_AVERAGE_DONATION,
    amount,
    amountPerCause,
  };
}

export function setAmountFromForm(amount) {
  return {
    type: types.MODAL_SET_AMOUNT_FROM_FORM,
    amount,
  };
}

export function setAmountWithGiftAid(amount) {
  return {
    type: types.AMOUNT_WITH_GIFT_AID,
    amount,
  };
}

export function receivedAllocations(allocations = List()) {
  return {
    type: types.RECEIVED_ALLOCATIONS,
    allocations,
  };
}

export function receivedCentralizedGifts(centralizedGifts = List()) {
  return {
    type: types.RECEIVED_CENTRALIZED_GIFTS,
    centralizedGifts,
  };
}

export function definitelyStopLoader() {
  return {
    type: types.MODAL_DEFINITELY_STOP_LOADER,
  };
}

export function startLoader() {
  return {
    type: types.MODAL_START_LOADER,
  };
}

export function errorReceiving() {
  return {
    type: types.MODAL_ERROR_RECEIVING,
  };
}

export function resetAllocations() {
  return {
    type: types.RESET_ALLOCATIONS,
  };
}

export function resetSelectedAllocations() {
  return {
    type: types.RESET_SELECTED_ALLOCATIONS,
  };
}

export function deleteSelectedAllocations() {
  return {
    type: types.DELETE_SELECTED_ALLOCATIONS,
  };
}

export function selectAllocation(allocation, bool, totalAmount) {
  return {
    type: types.SELECT_ALLOCATION,
    allocation,
    bool,
    totalAmount,
  };
}

export function selectCartAllocations() {
  return {
    type: types.SELECT_CART_ALLOCATIONS,
  };
}

export function setNewAmount(amount) {
  return {
    type: types.MODAL_SET_NEW_AMOUNT,
    amount,
  };
}

export function updateAllocationAmount(allocation, amount) {
  return {
    type: types.MODAL_SET_AMOUNT,
    allocation,
    amount,
  };
}

export function setFeeAmount(feeAmount) {
  return {
    type: types.SET_FEE_AMOUNT,
    feeAmount,
  };
}

export function donationTokenFetched(donationToken) {
  return {
    type: types.MODAL_SET_DONATION_TOKEN,
    donationToken,
  };
}

export function setDonationId(id) {
  return {
    type: types.MODAL_SET_DONATION_ID,
    id,
  };
}

export function fetchedAllocations() {
  return {
    type: types.FETCHED_ALLOCATIONS,
  };
}

export const triggerIsMounted = () => {
  return {
    type: types.TRIGGER_IS_MOUNTED,
  };
};

export const resetSubmitError = () => {
  return {
    type: types.RESET_SUBMIT_ERROR,
  };
};

const formatCauseAllocation = (causes, totalAmount) => {
  const obj = {};

  causes.map((cause) => {
    obj[cause.get("id")] = {
      percentage: (parseFloat(cause.get("amount")) / parseFloat(totalAmount)) * 100,
      amount: cause.get("amount"),
    };
  });

  return obj;
};

function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  const error = new Error(response.statusText);
  error.response = response;

  throw {
    response,
    error,
  };
}

export const fetchDonationToken = () => {
  return (dispatch) => {
    request(`/api/tokens`).then(({ data }) => {
      dispatch(donationTokenFetched(data.data));
    });
  };
};

export const buildUrl = ({
  campaignId,
  allocationType,
  entityId = "",
  page,
  query,
  category,
  limit,
  preselectedFundExId,
  fundsPage,
}) => {
  const baseUrl = "/microsite/api/campaigns";
  let targetUrl = "";

  if (allocationType === "campaign") {
    targetUrl = `${baseUrl}/donation_list?entity_id=${entityId}`;

    if (category) targetUrl += `&category=${encodeURIComponent(category)}`;
  } else {
    targetUrl = `${baseUrl}/${campaignId}/causes?`;
    fundsPage = -1;
  }

  if (page && fundsPage === -1) targetUrl += `&page=${page}`;
  if (fundsPage > 0) targetUrl += `&page=${fundsPage}`;
  if (query) targetUrl += `&query=${query}`;
  if (limit) targetUrl += `&limit=${limit}`;
  if (preselectedFundExId) targetUrl += `&preselectedFundExId=${preselectedFundExId}`;

  return targetUrl;
};

export const fetchAllocations = (
  campaignId,
  allocationType,
  options = {
    initialSearch: false,
    entityId: "",
    nextPage: false,
    query: null,
    category: null,
    page: null,
    limit: null,
    resetPage: false,
    preselectedFundExId: null,
    fundsPage: -1,
  },
) => {
  const {
    initialSearch,
    entityId,
    nextPage,
    limit,
    resetPage,
    preselectedFundExId,
    fundsPage,
  } = options;

  return (dispatch, getState) => {
    dispatch(startLoader());
    dispatch(togglePaginationLoading({ scope: GIVING_FORM_PAGINATION_SCOPE }, true));

    const startTime = Date.now();

    let page = getPage("giving_form")(getState());

    if (resetPage) {
      dispatch(resetAllocations());
      dispatch(resetPagination({ scope: GIVING_FORM_PAGINATION_SCOPE }));
      page = 1;
    }

    if (nextPage) {
      dispatch(nextPaginationPage({ scope: GIVING_FORM_PAGINATION_SCOPE }));
      page += 1;
    }

    const query = getSearchQuery(getState());
    const category = getSelectedCategory(getState());
    const buildableUrl = buildUrl({
      campaignId,
      allocationType,
      entityId,
      page,
      query,
      category,
      limit,
      preselectedFundExId,
      fundsPage,
    });

    return request(buildableUrl)
      .then(({ data }) => {
        const endTime = Date.now();

        // toggle is loading to false if we have waited a long enough time
        // to make the ui more fluid
        const dataItems = data[`${allocationType}s`];

        dispatch(receivedCentralizedGifts(data.centralized_gifts));
        dispatch(togglePaginationLoading({ scope: GIVING_FORM_PAGINATION_SCOPE }, false));

        if (endTime - startTime < MINIMUM_WAIT_FOR_RESPONSE) {
          const promise = new Promise((resolve) => {
            setTimeout(() => {
              if (initialSearch) {
                dispatch(fetchedAllocations());
                dispatch(
                  setInitialPagination({
                    scope: GIVING_FORM_PAGINATION_SCOPE,
                    total: parseInt(data.meta.total),
                    limit,
                  }),
                );
              }
              dispatch(definitelyStopLoader());
              dispatch(receivedAllocations(dataItems));

              resolve({
                total: data.meta.total,
              });
            }, WAIT_TO_SHOW_CONTENT);
          });

          return promise.then((res) => res);
        }

        dispatch(definitelyStopLoader());
        dispatch(receivedAllocations(dataItems));

        return {
          total: data.meta.total,
        };
      })
      .catch((err) => {
        const { res } = err;

        if (res) {
          dispatch(errorReceiving(res.data.errors));
          dispatch(definitelyStopLoader());

          if (initialSearch) {
            dispatch(fetchedAllocations());
          }
        } else {
          throw err;
        }
      });
  };
};

export function updateCustomFields(opts = { customFields: {} }) {
  const { campaignId, donationId, customFields } = opts;

  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  const formattedCustomFields = {
    donation: {
      custom_fields_hash: JSON.stringify(customFields),
    },
  };

  return (dispatch) => {
    dispatch(startLoader());
    return request(`/donations/${donationId}`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
      },
      method: "PATCH",
      data: formattedCustomFields,
    })
      .then((res) => {
        dispatch(definitelyStopLoader());
        dispatch(nextPage());
      })
      .catch((err) => {
        dispatch(definitelyStopLoader());
        dispatch(errorReceiving(err));
        throw err;
      });
  };
}

export function submittingForm() {
  return {
    type: types.MODAL_SUBMITTING_FORM,
  };
}

export function formSubmitted(error) {
  return {
    type: types.MODAL_FORM_SUBMITTED,
    error,
  };
}

export function setConfirming(isConfirming) {
  return {
    type: types.CONFIRMING_CHANGED,
    isConfirming,
  };
}

export function setAddingGift(isAddingGift) {
  return {
    type: types.ADDING_GIFT_CHANGED,
    isAddingGift,
  };
}

export function setIsGiftAid(isGiftAid) {
  return {
    type: types.IS_GIFT_AID,
    isGiftAid,
  };
}

export function setStripePaymentId(id) {
  return {
    type: types.STRIPE_PAYMENT_ID_CHANGED,
    id,
  };
}

export function setConnectedStripeAccountId(id) {
  return {
    type: types.STRIPE_CONNECTED_ACCOUNT_ID,
    id,
  };
}

export function setStripePaymentIntentId(paymentIntentId) {
  return {
    type: types.STRIPE_PAYMENT_INTENT_ID,
    paymentIntentId,
  };
}

export function setStripePublishableKey(publishableKey) {
  return {
    type: types.STRIPE_PUBLISHABLE_KEY,
    publishableKey,
  };
}

export function setStripeObject(stripe) {
  return {
    type: types.STRIPE_OBJECT,
    stripe,
  };
}

export function setStripeElementsObject(elements) {
  return {
    type: types.STRIPE_ELEMENTS_OBJECT,
    elements,
  };
}

export function setStripeClientSecretKey(id) {
  return {
    type: types.STRIPE_CLIENT_SECRET_KEY,
    id,
  };
}

export const setMinimumValueError = (error) => {
  return {
    type: types.SET_MINIMUM_VALUE_ERROR,
    error,
  };
};

const formatCampaignAllocations = (campaigns, total, addedCentralizedGiftIds) => {
  return campaigns.reduce((campaigns, campaign) => {
    if (campaign.get("amount")) {
      campaigns.push({
        id: campaign.get("id"),
        total_amount: campaign.get("amount"),
        added_centralized_gift: addedCentralizedGiftIds.indexOf(campaign.get("id")) != -1,
      });
    }
    return campaigns;
  }, []);
};

const formatValues = ({
  allocationType,
  campaignId,
  attrs,
  selectedAllocations,
  totalAmount,
  spreedlyToken,
  isStripeSepaDebit,
  addedCentralizedGifts,
  interval,
}) => {
  if (
    attrs.hasOwnProperty("month") &&
    attrs.hasOwnProperty("year") &&
    attrs.hasOwnProperty("card_exp")
  ) {
    delete attrs.card_exp;
    delete attrs.month;
    delete attrs.year;
  }

  if (attrs.hasOwnProperty("appeal")) {
    attrs.appeal_id = attrs.appeal;
    delete attrs.appeal;
  }

  if (attrs.isPublic === true || attrs.isPublic === false) {
    attrs.public = attrs.isPublic;
    delete attrs.isPublic;
  }

  attrs.campaign_id = campaignId;
  attrs.spreedly_cc_token = spreedlyToken;
  attrs.interval = interval;
  if (isStripeSepaDebit) {
    attrs.don_type = "direct_debit";
  }

  if (attrs.custom_fields_hash) {
    attrs.custom_fields_hash = JSON.stringify(attrs.custom_fields_hash);
  }

  const donation = {};

  if (allocationType === "campaign") {
    var addedCentralizedGiftIds = [];

    if (addedCentralizedGifts) {
      addedCentralizedGiftIds = addedCentralizedGifts
        .map((x) => x.allocation.toJS().id)
        .join(",");
    }
    donation.campaigns = formatCampaignAllocations(
      selectedAllocations,
      totalAmount,
      addedCentralizedGiftIds,
    );
  } else {
    donation.causes_allocation_hash = JSON.stringify(
      formatCauseAllocation(selectedAllocations, totalAmount),
    );
  }

  return {
    donation: {
      ...donation,
      ...attrs,
    },
  };
};

// Dead code: every branch below targets an endpoint that cannot serve a request.
// /multi_donate is not routed at all, and /donations and /bbms_donate route to
// DonationsController#create and #bbms_create, neither of which exists. The live
// donor path is api/v2/donations. Tracked for removal separately; see FUN-48139.
const formatUrl = (allocationType, paymentType = null) => {
  if (allocationType === "campaign") {
    return "/multi_donate";
  }

  return paymentType !== "bbms" ? "/donations" : "/bbms_donate";
};

const submitAnalytics = ({ transactionId, allocations, total }) => {
  window.SnapAnalytics &&
    window.SnapAnalytics.addTransaction({
      id: transactionId,
      revenue: total,
      items: allocations,
    });

  allocations.forEach((allocation) => {
    window.SnapAnalytics &&
      window.SnapAnalytics.addItem({
        id: allocation.get("id"),
        name: allocation.get("name"),
        price: allocation.get("amount"),
      });
  });

  window.SnapAnalytics && window.SnapAnalytics.send();
};

export const cancelStripePaymentIntent = (entityId, stripePaymentIntentId, dispatch) => {
  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  const param = {
    stripe_payment_intent_id: stripePaymentIntentId,
  };

  return request(
    "/stripe/cancel_payment_intent",
    mapPostRequestParams(entityId, token, param),
  )
    .then((response) => {})
    .catch((error) => {
      if (error) {
        dispatch(formSubmitted(error.response.data.message));
      }
    });
};

export const createAbandonForm = (entityId, campaignId, first_name, last_name, email) => {
  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  const params = {
    campaign_id: campaignId,
    first_name: first_name,
    last_name: last_name,
    email: email,
  };

  return request("/abandon_form/create", mapPostRequestParams(entityId, token, params))
    .then((result) => {
      return result;
    })
    .catch((error) => {
      if (error) {
        return;
      }
    });
};

export const createStripeSubscription = (
  entityId,
  customerId,
  paymentMethodId,
  interval,
  occurrences,
  totalAmount,
  dispatch,
) => {
  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  const params = {
    customerId: customerId,
    paymentMethodId: paymentMethodId,
    interval: interval,
    occurrences: occurrences,
    amount: totalAmount,
  };

  return request(
    "/stripe/create_subscription",
    mapPostRequestParams(entityId, token, params),
  )
    .then((result) => {
      return result;
    })
    .catch((error) => {
      if (error) {
        dispatch(formSubmitted(error.response.data.message));
      }
    });
};

export const createStripeCustomer = (
  email,
  first_name,
  last_name,
  entityId,
  dispatch,
) => {
  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  const params = {
    email: email,
    first_name: first_name,
    last_name: last_name,
  };

  return request("/stripe/create_customer", mapPostRequestParams(entityId, token, params))
    .then((responseCustomer) => {
      if (!responseCustomer) {
        return;
      }

      return responseCustomer.data;
    })
    .catch((err) => {
      if (error) {
        dispatch(formSubmitted(error.response.data.message));
      }
    });
};

export const getBbmsPaymentKeys = (entityId, dispatch) => {
  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  return request("/bbms/payment_keys", mapGetRequestParams(entityId, token, {}))
    .then((responsePaymentKeys) => {
      if (!responsePaymentKeys) {
        return;
      }

      return responsePaymentKeys.data;
    })
    .catch((err) => {
      dispatch(
        formSubmitted("An error occurred when trying to pull out the bbms payment keys"),
      );
    });
};

export const getStripeToken = (entityId, dispatch) => {
  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  return request("/stripe/get_stripe_token", mapGetRequestParams(entityId, token, {}))
    .then((responseDataToken) => {
      if (!responseDataToken) {
        return;
      }

      return responseDataToken.data;
    })
    .catch((err) => {
      dispatch(
        formSubmitted(
          "An error occurred when trying to pull out the client_secret and stripe_uid key",
        ),
      );
    });
};

export const getAccountStripeId = (
  amount,
  entityId,
  dispatch,
  stripePaymentIntentId = "",
) => {
  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  if (stripePaymentIntentId) {
    const updateParams = {
      amount: amount,
      stripe_payment_intent_id: stripePaymentIntentId,
    };

    return request(
      "/stripe/update_payment_intent",
      mapPostRequestParams(entityId, token, updateParams),
    )
      .then((responseDataAccount) => {
        if (!responseDataAccount) {
          return;
        }

        return responseDataAccount.data;
      })
      .catch((err) => {
        dispatch(
          formSubmitted(
            "An error occurred when trying to pull out the client_secret and stripe_uid key",
          ),
        );
      });
  } else {
    const amountParam = { amount };
    return request(
      "/stripe/create_payment_intent",
      mapPostRequestParams(entityId, token, amountParam),
    )
      .then((responseDataAccount) => {
        if (!responseDataAccount) {
          return;
        }

        return responseDataAccount.data;
      })
      .catch((err) => {
        dispatch(
          formSubmitted(
            "An error occurred when trying to pull out the client_secret and stripe_uid key",
          ),
        );
      });
  }
};

export const saveStripeDataPaymentMethod = (
  entityId,
  donationIds,
  payment_method_id,
  currency_code,
  payment_intent_id,
  dispatch,
) => {
  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const token = csrfTokenMeta ? csrfTokenMeta.content : "";

  const stripeParams = {
    donation: {
      donation_ids: donationIds,
      stripe_payment_method_id: payment_method_id,
      currency_code: currency_code,
      stripe_payment_intent_id: payment_intent_id,
    },
  };

  return request(
    "/save_stripe_data_payment_method",
    mapPostRequestParams(entityId, token, stripeParams),
  )
    .then((response) => {
      if (!response) {
        return;
      }

      dispatch(nextPage());
      dispatch(formSubmitted());
      return response.data;
    })
    .catch((err) => {
      dispatch(
        formSubmitted(
          "An error occurred when trying save the Stripe data payment method",
        ),
      );
    });
};

export const submitGivingForm = (
  opts = {
    campaignId: null,
    campaignName: null,
    dispatch: null,
    donationToken: "",
    attrs: null,
    selectedAllocations: List(),
    totalAmount: null,
    spreedlyToken: null,
    paymentType: null,
    isStripePayment: false,
    isBraintreePayment: false,
    handleStripeSubmit: null,
    entityId: null,
    isStripeSepaDebit: false,
    addedCentralizedGifts: List(),
    interval: null,
  },
) => {
  const {
    campaignId,
    dispatch,
    donationToken,
    attrs,
    selectedAllocations,
    totalAmount,
    allocationType,
    spreedlyToken,
    paymentType,
    isStripePayment,
    isBraintreePayment,
    handleStripeSubmit,
    entityId,
    isStripeSepaDebit,
    addedCentralizedGifts,
    interval,
  } = opts;

  dispatch(submittingForm());

  if (!campaignId) {
    dispatch(setConfirming(false));
    throw new Error("Must supply a campaign id");
  }

  const csrfTokenMeta = document.querySelector('meta[name="csrf-token"]');
  const csrfToken = csrfTokenMeta ? csrfTokenMeta.content : "";

  const donationObject = formatValues({
    allocationType,
    campaignId,
    attrs,
    selectedAllocations,
    totalAmount,
    spreedlyToken,
    isStripeSepaDebit,
    addedCentralizedGifts,
    interval,
  });

  let url = formatUrl(allocationType, paymentType);

  return request(url, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${donationToken}`,
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    method: "POST",
    data: donationObject,
  })
    .then((res) => {
      if (res && res.data && res.data.donation) {
        submitAnalytics({
          transactionId: res.data.donation.id,
          allocations: selectedAllocations,
          total: totalAmount,
        });
      }
      if (typeof window.EventAnalyticsService != "undefined") {
        window.EventAnalyticsService.addEvent({
          name: "GIVING FORM - DONATION COMPLETE",
          giving_form_version: "React",
        });
      }

      const donationId = res && res.data && res.data.donation ? res.data.donation.id : "";
      const donationIds =
        res && res.data && res.data.donation_ids ? res.data.donation_ids : [];
      const isStripeCardWallet = res && res.data ? res.data.is_stripe_card_wallet : false;

      dispatch(setDonationId(donationId));

      if (
        (isStripePayment && !isStripeCardWallet && !attrs.interval) ||
        isStripeSepaDebit
      ) {
        if (donationIds.length === 0 && donationId) {
          donationIds.push(donationId);
        }

        if (isStripeSepaDebit) {
          handleStripeSepaDebitSubmit(
            entityId,
            totalAmount,
            donationIds,
            attrs.interval,
            attrs.occurrences,
          );
          return;
        }

        handleStripeSubmit(attrs, dispatch, donationIds);
        return;
      }

      const braintreeNonce =
        res && res.data && res.data.braintree_nonce
          ? res.data.braintree_nonce
          : undefined;
      if (isBraintreePayment) {
        if (!braintreeNonce) {
          throw "braintree payment method nonce is undefined";
        }
        const dIds = donationIds.length === 0 ? [donationId] : donationIds;
        createBraintreeTransaction(
          dispatch,
          csrfToken,
          entityId,
          braintreeNonce,
          dIds,
          totalAmount,
          attrs,
        );
      } else {
        dispatch(nextPage());
        dispatch(formSubmitted());
      }
    })
    .catch((err) => {
      const { response } = err;
      const errors = response && response.data && response.data.errors;

      if (typeof window.EventAnalyticsService != "undefined") {
        const error =
          errors && errors.base ? errors.base[0] : "Error processing your donation";
        window.EventAnalyticsService.addEvent({
          name: "GIVING FORM - DONATION FAILURE",
          giving_form_version: "React",
          error,
        });
      }

      const errorMessage = getErrorFirstMessage(errors);
      if (errors) {
        if (attrs.custom_fields_hash && typeof attrs.custom_fields_hash === "string") {
          attrs.custom_fields_hash = JSON.parse(attrs.custom_fields_hash);
        }
        if (errors.base) {
          dispatch(formSubmitted(errors.base[0]));
        } else {
          dispatch(formSubmitted(errorMessage));
        }
        handleProcessorErrors(errors);
        dispatch(setConfirming(false));
      } else {
        dispatch(formSubmitted(errorMessage));
        dispatch(setConfirming(false));

        throw err;
      }
    });
};

const handleStripeSepaDebitSubmit = async (
  entityId,
  totalAmount,
  donationIds,
  interval,
  occurrences,
) => {
  const params = {
    entity_id: entityId,
    donation_ids: donationIds,
    total_amount: totalAmount,
    interval: interval,
    occurrences: occurrences,
    location_href: window.location.href,
  };
  const responce = await bacsDebitCheckout(params);
  const { url } = responce.data;
  window.location.href = url;
};

const createBraintreeTransaction = (
  dispatch,
  csrfToken,
  entityId,
  braintreeNonce,
  donationIds,
  totalAmount,
  attrs,
) => {
  const param = {
    braintree_nonce: braintreeNonce,
    donation_ids: donationIds,
    total_amount: totalAmount,
    interval: attrs.interval,
    occurrences: attrs.occurrences,
    first_name: attrs.first_name,
    last_name: attrs.last_name,
  };

  return request(
    "/braintree/settle_transaction",
    mapPostRequestParams(entityId, csrfToken, param),
  )
    .then((response) => {
      dispatch(nextPage());
      dispatch(formSubmitted());
    })
    .catch((err) => {
      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.errors &&
        err.response.data.errors.length
      ) {
        dispatch(formSubmitted(err.response.data.errors[0]));
      }
    });
};

const getErrorFirstMessage = (errors) => {
  return errors && Array.isArray(errors) && errors.length > 0 && errors[0]
    ? errors[0]
    : "Error processing your donation";
};

const mapGetRequestParams = (entityId, token, amountParam) => {
  return {
    headers: getHeadersParams(entityId, token),
    method: "GET",
    data: JSON.stringify(amountParam),
  };
};

const mapPostRequestParams = (entityId, token, amountParam) => {
  return {
    headers: getHeadersParams(entityId, token),
    method: "POST",
    data: amountParam,
  };
};

const getHeadersParams = (entityId, token) => {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    "ENTITY-ID": entityId,
    "X-CSRF-Token": token,
  };
};
